import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  inject,
  signal,
  computed,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { distinctUntilChanged, map } from 'rxjs';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import Swal from 'sweetalert2';
import { CambiarAreaModal } from '../../../components/cambiar-area-modal/cambiar-area-modal';
import { DocumentosModal } from '../../../components/documentos-modal/documentos-modal';
import { RH_ACTIONS } from '@shared/constants/rh-actions.constants';
import { EmpleadoService } from '@services/empleado.service';
import { CampoExpediente, EmpleadoList } from '@schemas/empleado.schema';

import {
  CellStack,
  PageHeader,
  StatChip,
  TableActions,
  type ActionButtonDef,
  DataTable,
  CellTemplateDirective,
  type ColumnDef,
  type DataTableQuery,
  type TableAction,
} from '@intelishs-group/ui';

/**
 * La fila de la tabla: la del contrato más lo que la página deriva de
 * `camposFaltantes`. `faltan` existe para poder ORDENAR por la columna (el
 * data-table ordena por la `key`, no por lo que pinta la plantilla) y
 * `faltantesTexto` para el Excel, que no lleva columnas `custom`.
 */
type FilaEmpleado = EmpleadoList & { faltan: number; faltantesTexto: string };

/** Un chip de «les falta»: el campo del backend con su conteo sobre la vista actual. */
interface ChipFaltante extends CampoExpediente {
  faltan: number;
}

@Component({
  selector: 'app-empleados-lista',
  imports: [
    DataTable,
    CellTemplateDirective,
    TableActions,
    CambiarAreaModal,
    DocumentosModal,
    PageHeader,
    CellStack,
    StatChip,
    NzTooltipModule,
    NzButtonModule,
    NzIconModule,
  ],
  templateUrl: './empleados-lista.page.html',
  styleUrl: './empleados-lista.page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmpleadosListaPage implements OnInit {
  private readonly empleadoService = inject(EmpleadoService);
  private readonly router = inject(Router);
  private readonly message = inject(NzMessageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ruta = inject(ActivatedRoute);

  empleados = signal<FilaEmpleado[]>([]);
  loading = signal(false);

  /** Los campos que el backend puede reportar como faltantes, en el orden del tooltip. */
  readonly campos = signal<CampoExpediente[]>([]);

  // ── Filtros: DOS ejes que se combinan ──────────────────────────────
  // «los activos sin jefe» es la pregunta normal de esta pantalla, así que
  // estado y faltante son independientes y no un único grupo excluyente.
  /** `null` los dos, `true` solo activos, `false` solo inactivos. */
  readonly estado = signal<boolean | null>(null);
  /** Clave del campo por el que se filtra, o `null`. */
  readonly faltante = signal<string | null>(null);
  /** El panel de «Les falta». Plegado no quita el filtro: lo marca el circular. */
  readonly panelAbierto = signal(false);

  /**
   * La vista de la tabla (búsqueda, página, orden), en manos de esta página para
   * poder escribirle la búsqueda que llega de «Ir a…» (`?buscar=`). El
   * `pageSize` es el mismo `[pageSize]` de la plantilla.
   */
  readonly query = signal<DataTableQuery>({
    search: '',
    pageIndex: 1,
    pageSize: 15,
    sortKey: null,
    sortOrder: null,
  });

  /** Filas del estado elegido. Sobre ellas se cuentan los faltantes. */
  private readonly delEstado = computed(() => {
    const estado = this.estado();
    const filas = this.empleados();
    return estado === null ? filas : filas.filter((e) => e.activo === estado);
  });

  readonly empleadosFiltrados = computed(() => {
    const campo = this.faltante();
    const filas = this.delEstado();
    return campo === null ? filas : filas.filter((e) => e.camposFaltantes.includes(campo));
  });

  /**
   * Un chip por campo, contado sobre el estado elegido: con «Activos» encendido
   * cuenta los huecos de los activos, que es lo que RH va a trabajar.
   *
   * De mayor a menor, y los que están en cero se ocultan —un chip que no filtra
   * nada solo ocupa lugar—, salvo el que esté activo: si desapareciera, no
   * habría cómo quitar el filtro.
   */
  readonly chipsFaltantes = computed<ChipFaltante[]>(() => {
    const filas = this.delEstado();
    const activo = this.faltante();
    return this.campos()
      .map((c) => ({
        ...c,
        faltan: filas.filter((e) => e.camposFaltantes.includes(c.campo)).length,
      }))
      .filter((c) => c.faltan > 0 || c.campo === activo)
      .sort((a, b) => b.faltan - a.faltan);
  });

  // ── Modal "Cambiar Área" ──────────────────────────────────────────
  cambiarAreaVisible = signal(false);
  empleadoParaCambio = signal<EmpleadoList | null>(null);

  // ── Modal "Documentos" ────────────────────────────────────────────
  documentosVisible = signal(false);
  empleadoParaDocs = signal<EmpleadoList | null>(null);

  /**
   * Anchos calculados sobre el valor más largo de cada campo, para que el
   * contenido no se corte ni haga wrap (la excepción es la dirección: hay
   * valores de 50 caracteres, así que se trunca con tooltip en `cell-stack`).
   *
   * Antes eran 10 columnas sumando 1360px de mínimo: la tabla vivía con scroll
   * horizontal. Unificando en dos líneas quedan 6 columnas y ~960px.
   */
  readonly columns = computed<ColumnDef<FilaEmpleado>[]>(() => [
    // Abre la fila: el estado es la referencia con la que el usuario se ubica y
    // por la que filtra con las pestañas de la barra.
    //
    // Encabezado vacío y 44px: el contenido es un punto de 10px con tooltip, así
    // que la palabra "Estatus" era lo único que pedía espacio —el texto medía
    // más que el dato— y no aporta nada que el color no diga ya.
    {
      key: 'activo',
      header: '',
      width: '44px',
      align: 'center',
      type: 'status',
    },
    // Columna flexible: se queda con el ancho sobrante, que es donde rinde
    // (nombre de 33 chars sobre "MC#### · puesto" de 41).
    {
      key: 'nombreCompleto',
      header: 'Empleado',
      width: 'auto',
      type: 'custom',
      // El `cell-stack` no trunca solo: sin el flag el nombre largo ensancha
      // la columna y desborda la tabla en vez de recortarse.
      ellipsis: true,
      filterable: true,
      sortable: true,
    },
    // Va pegada a Empleado: nombre → puesto → área → dirección se lee como una
    // sola secuencia organizacional. También flexible, porque la dirección
    // llega a 50 chars y agradece el espacio sobrante para truncar menos.
    {
      key: 'area',
      header: 'Área / Dirección',
      width: 'auto',
      type: 'custom',
      // Aqui es donde mas rinde: la direccion llega a 50 chars.
      ellipsis: true,
      filterable: true,
    },
    // Identificador fiscal: se consulta puntualmente, no se escanea, así que
    // va después de los datos con los que se reconoce a la persona.
    // Encabezado centrado sobre la columna; el RFC en sí se queda a la
    // izquierda, alineado con el resto de los datos de texto.
    { key: 'rfc', header: 'RFC', width: '125px', headerAlign: 'center', filterable: true },
    // «Faltan de N» y no «Faltan»: el número necesita su escala. La N sale de
    // la lista del backend y por eso `columns` es un computed; mientras esa
    // lista no llega, el encabezado dice solo «Faltan» en vez de «de 0».
    {
      key: 'faltan',
      header: this.campos().length ? `Faltan de ${this.campos().length}` : 'Faltan',
      width: '112px',
      align: 'center',
      type: 'custom',
      sortable: true,
    },
    // 130px y no 115px: "Fecha Ingreso" más la flecha de ordenamiento no
    // caben en 115, y la flecha se iba al siguiente renglón —el encabezado
    // ocupaba dos líneas y desalineaba la fila de títulos—.
    {
      key: 'fechaIngreso',
      header: 'Fecha Ingreso',
      width: '130px',
      type: 'date',
      sortable: true,
    },
    // 5 botones de 24px (contrato del theme) + 4 gaps de 6px = 144px, más el
    // padding de celda. Es la botonera más grande del sistema.
    {
      key: 'acciones',
      header: 'Acciones',
      width: '152px',
      align: 'center',
      type: 'custom',
    },
  ]);

  /**
   * Buscables sin columna propia: CURP e ID interno siguen siendo localizables
   * (se pegan en el buscador para validar identidad) y dirección/puesto se
   * buscan aunque vivan en la línea meta de otra celda.
   */
  readonly extraFilterKeys = ['curp', 'idInterno', 'puesto', 'direccion'];

  /**
   * El Excel sí lleva el expediente completo: la tabla se compacta para leerse
   * en pantalla, pero el archivo es para trabajar los datos fuera. Repone lo
   * que en pantalla vive dentro de una celda unificada (nombre, área) o no
   * tiene columna (ID, CURP, puesto, dirección).
   */
  readonly exportColumns: ColumnDef<FilaEmpleado>[] = [
    // En pantalla es un punto sin encabezado; en el archivo necesita nombre.
    { key: 'activo', header: 'Estatus', type: 'status' },
    { key: 'idInterno', header: 'ID Interno' },
    { key: 'nombreCompleto', header: 'Nombre Completo' },
    { key: 'curp', header: 'CURP' },
    { key: 'puesto', header: 'Puesto' },
    { key: 'area', header: 'Área' },
    { key: 'direccion', header: 'Dirección' },
    { key: 'faltan', header: 'Datos faltantes' },
    { key: 'faltantesTexto', header: 'Qué falta' },
  ];

  getActionsForRow(row: EmpleadoList): ActionButtonDef[] {
    return [
      RH_ACTIONS.ver,
      RH_ACTIONS.editar,
      // Cambiar Área y Documentos solo en empleados activos — para inactivos
      // no tiene sentido reasignar área antes del reingreso, ni gestionar
      // documentos nuevos.
      ...(row.activo ? [RH_ACTIONS.cambiarArea] : []),
      ...(row.activo ? [RH_ACTIONS.documentos] : []),
      ...(row.activo ? [RH_ACTIONS.desactivar] : [RH_ACTIONS.activar]),
    ];
  }

  onAction(event: TableAction<EmpleadoList>): void {
    switch (event.action) {
      case 'ver':
        this.verEmpleado(event.row.id);
        break;
      case 'editar':
        this.editarEmpleado(event.row.id);
        break;
      case 'cambiarArea':
        this.abrirCambiarArea(event.row);
        break;
      case 'documentos':
        this.abrirDocumentos(event.row);
        break;
      case 'desactivar':
        this.confirmarDesactivar(event.row);
        break;
      case 'activar':
        this.confirmarActivar(event.row);
        break;
    }
  }

  abrirCambiarArea(emp: EmpleadoList): void {
    this.empleadoParaCambio.set(emp);
    this.cambiarAreaVisible.set(true);
  }

  abrirDocumentos(emp: EmpleadoList): void {
    this.empleadoParaDocs.set(emp);
    this.documentosVisible.set(true);
  }

  /** Callback del modal cuando termina exitosamente — recarga la lista. */
  onAreaCambiada(): void {
    this.cargarEmpleados();
  }

  // ── Contadores ──────────────────────────────────────────────────
  totalEmpleados = computed(() => this.empleados().length);
  totalActivos = computed(() => this.empleados().filter((e) => e.activo).length);
  totalInactivos = computed(() => this.empleados().filter((e) => !e.activo).length);

  /** Las tres pestañas del estado, con su conteo sobre el total. */
  readonly opcionesEstado = computed(() => [
    { etiqueta: 'Todos', valor: null, total: this.totalEmpleados() },
    { etiqueta: 'Activos', valor: true, total: this.totalActivos() },
    { etiqueta: 'Inactivos', valor: false, total: this.totalInactivos() },
  ]);

  /** Expedientes con algún hueco, sobre el estado elegido. */
  private readonly incompletos = computed(
    () => this.delEstado().filter((e) => e.camposFaltantes.length > 0).length,
  );

  /**
   * Con el panel plegado, los conteos de «Les falta» dejan de verse; el tooltip
   * conserva el total para que la presión de completar expedientes no se pierda.
   */
  readonly tooltipPanel = computed(() =>
    this.panelAbierto()
      ? 'Ocultar «Les falta»'
      : `Les falta: ${this.incompletos()} expedientes incompletos`,
  );

  ngOnInit(): void {
    // `?buscar=` de «Ir a…» (Ctrl+K): el texto va al buscador de la tabla y los
    // filtros vuelven a «Todos» —quien buscó a alguien no eligió además «Inactivos»—.
    // SE ESCUCHA y no sólo se lee al entrar: Ctrl+K navega aquí estando ya en la
    // lista, el router reusa la página y `ngOnInit` no vuelve a correr.
    this.ruta.queryParamMap
      .pipe(
        map((p) => p.get('buscar')),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((buscar) => {
        if (buscar === null) return;
        this.estado.set(null);
        this.faltante.set(null);
        this.query.update((q) => ({ ...q, search: buscar.trim(), pageIndex: 1 }));
      });

    this.empleadoService
      .getCamposExpediente()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((campos) => {
        this.campos.set(campos);
        // Las etiquetas del texto del Excel salen de esta lista: si llegó
        // después que los empleados, se rearman.
        this.empleados.update((filas) => filas.map((e) => this.aFila(e)));
      });
    this.cargarEmpleados();
  }

  // ── Interacción con los chips de «Les falta» ────────────────────
  // Cada chip enciende y apaga su filtro: uno que solo se puede poner obliga a
  // recargar para quitarlo.

  alternarFaltante(campo: string): void {
    this.faltante.update((actual) => (actual === campo ? null : campo));
  }

  /** Tooltip del chip: el conteo con su escala, y qué cuenta cuando no es obvio. */
  tooltipChip(c: ChipFaltante): string {
    const base = `${c.faltan} de ${this.delEstado().length} sin ${c.etiqueta.toLowerCase()}`;
    return c.descripcion ? `${base}. ${c.descripcion}` : base;
  }

  /**
   * Qué le falta a esta persona, por nombre. El número dice cuántos; esto dice
   * cuáles. Se recorre `campos()` y no `camposFaltantes` para que el orden sea
   * siempre el mismo.
   */
  queLeFalta(fila: FilaEmpleado): string {
    if (fila.faltan === 0) return 'Expediente completo';
    const nombres = this.etiquetas(fila);
    return nombres.length
      ? 'Sin capturar: ' + nombres.join(', ')
      : `Le faltan ${fila.faltan} datos`;
  }

  private etiquetas(fila: EmpleadoList): string[] {
    const faltan = new Set(fila.camposFaltantes);
    return this.campos()
      .filter((c) => faltan.has(c.campo))
      .map((c) => c.etiqueta);
  }

  private aFila(e: EmpleadoList): FilaEmpleado {
    return {
      ...e,
      faltan: e.camposFaltantes.length,
      faltantesTexto: this.etiquetas(e).join(', '),
    };
  }

  /**
   * @param forzar lo usa el botón "Refrescar": sin él la llamada devuelve la
   * caché del servicio y el botón no traía nada nuevo.
   */
  cargarEmpleados(forzar = false): void {
    this.loading.set(true);
    this.empleadoService
      .getAll(forzar)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.empleados.set(data.map((e) => this.aFila(e)));
          this.loading.set(false);
        },
        error: () => {
          this.message.error('Error al cargar empleados');
          this.loading.set(false);
        },
      });
  }

  nuevoEmpleado(): void {
    this.router.navigate(['/empleados/nuevo']);
  }

  verEmpleado(id: number): void {
    this.router.navigate(['/empleados/detalle', id]);
  }

  editarEmpleado(id: number): void {
    this.router.navigate(['/empleados/editar', id]);
  }

  async confirmarDesactivar(emp: EmpleadoList): Promise<void> {
    const result = await Swal.fire({
      title: '¿Desactivar este empleado?',
      text: emp.nombreCompleto,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Desactivar',
      cancelButtonText: 'Cancelar',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'confirm-btnSweet',
        cancelButton: 'cancel-btnSweet',
        actions: 'swal-actions-spaced',
      },
    });
    if (result.isConfirmed) this.eliminarEmpleado(emp.id);
  }

  async confirmarActivar(emp: EmpleadoList): Promise<void> {
    const result = await Swal.fire({
      title: '¿Activar este empleado (reingreso)?',
      text: emp.nombreCompleto,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Activar',
      cancelButtonText: 'Cancelar',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'confirm-btnSweet',
        cancelButton: 'cancel-btnSweet',
        actions: 'swal-actions-spaced',
      },
    });
    if (result.isConfirmed) this.activarEmpleado(emp.id);
  }

  eliminarEmpleado(id: number): void {
    this.empleadoService
      .delete(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.message.success('Empleado desactivado');
          this.cargarEmpleados();
        },
        error: () => {
          this.message.error('Error al desactivar empleado');
        },
      });
  }

  activarEmpleado(id: number): void {
    this.empleadoService
      .activate(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.message.success('Empleado activado (reingreso)');
          this.cargarEmpleados();
        },
        error: () => {
          this.message.error('Error al activar empleado');
        },
      });
  }
}
